import { Fragment, useMemo } from "react";
import { Check, Minus } from "lucide-react";
import type { PermissionResponse, RoleResponse } from "../../../shared/types/role";
import { systemRoleLabel } from "../../../shared/format";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table";

interface Props {
  roles: RoleResponse[];
  permissions: PermissionResponse[];
}

/** Read-only N×M matrix of role-permission assignments grouped by permission group. */
export function RoleMatrix({ roles, permissions }: Props) {
  const byGroup = useMemo(() => {
    const map = new Map<string, PermissionResponse[]>();
    for (const p of permissions) {
      if (!map.has(p.groupVi)) map.set(p.groupVi, []);
      map.get(p.groupVi)!.push(p);
    }
    return Array.from(map.entries());
  }, [permissions]);

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Quyền hạn</TableHead>
            {roles.map((r) => (
              <TableHead key={r.id} className="text-center">
                {systemRoleLabel[r.code] ?? r.name}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {byGroup.map(([group, perms]) => (
            <Fragment key={`group-${group}`}>
              <TableRow className="bg-muted/50 hover:bg-muted/50">
                <TableCell
                  colSpan={roles.length + 1}
                  className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                >
                  {group}
                </TableCell>
              </TableRow>
              {perms.map((p) => (
                <TableRow key={p.code}>
                  <TableCell title={p.descriptionEn ?? ""} className="font-medium">
                    {p.labelVi}
                  </TableCell>
                  {roles.map((r) => (
                    <TableCell key={r.id} className="text-center">
                      {r.permissionCodes.includes(p.code) ? (
                        <Check className="mx-auto h-4 w-4 text-primary" aria-label="có" />
                      ) : (
                        <Minus className="mx-auto h-4 w-4 text-muted-foreground/40" aria-label="không" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </Fragment>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
